import { useEffect, useRef, useState } from "react";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import GripVertical from "lucide-react/dist/esm/icons/grip-vertical";
import Menu from "lucide-react/dist/esm/icons/menu";
import Palette from "lucide-react/dist/esm/icons/palette";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw";
import X from "lucide-react/dist/esm/icons/x";
import {
  AddMenu,
  ConfirmReset,
  ContextPanel,
  EditorAddButton,
  EditorRail,
  FloatingToolbar,
  ModeSwitch,
  Onboarding,
  ThemePanel,
} from "./components/Editor";
import { SECTION_REGISTRY } from "./components/Sections";
import { animationStyle, cn, sanitizeUrl, validateSiteShape } from "./lib";
import { useEditor } from "./store";
import { useReveal } from "./hooks";
import type { SiteConfig } from "./types";
import { GlobalEditableText } from "./components/Editable";

function SandboxIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 10.5 16 5l11 5.5v11L16 27 5 21.5z" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M6 11l10 5 10-5M16 16v10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M10.2 11.8c2.1-1.6 4.1-2.2 5.9-1.5 1.5.5 2.6.4 4.7-.8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function BrandWordmark({ light = false }: { light?: boolean }) {
  const { site, editing, preview, selection, select, updateSite } = useEditor();
  const enabled = editing && !preview;
  const id = "global:brandName";
  return (
    <a
      className={cn("logo sandbox-logo", light && "light")}
      href={enabled ? undefined : "#top"}
      aria-label={site.brand.ariaLabel}
      onClick={(event) => {
        if (!enabled) return;
        event.preventDefault();
        event.stopPropagation();
        select({ kind: "global", field: "brandName", elementId: id });
      }}
    >
      <SandboxIcon className="sandbox-mark" />
      <span
        className={cn(enabled && "cms-editable", selection?.elementId === id && "cms-selected")}
        contentEditable={enabled}
        suppressContentEditableWarning
        data-cms-id={id}
        data-cms-kind="global"
        data-cms-field="brandName"
        style={{
          fontSize: site.globalTextStyles?.brandName?.fontSize,
          fontWeight: site.globalTextStyles?.brandName?.fontWeight,
          color: site.globalTextStyles?.brandName?.color,
          letterSpacing: site.globalTextStyles?.brandName?.letterSpacing == null ? undefined : `${site.globalTextStyles.brandName.letterSpacing}px`,
        }}
        onBlur={(event) => {
          if (!enabled) return;
          updateSite((target) => {
            target.brand.name = event.currentTarget.textContent?.slice(0, 80) || "Sandbox";
          });
        }}
      >
        {site.brand.name}
      </span>
    </a>
  );
}

function Header() {
  const { site, editing, preview, selection, select, updateSite } = useEditor();
  const [menu, setMenu] = useState(false);
  return (
    <header className="site-header">
      <BrandWordmark />
      <nav className={menu ? "open" : ""}>
        {site.navigation.map((item) => (
          <a
            key={item.id}
            href={sanitizeUrl(item.href)}
            contentEditable={editing && !preview}
            suppressContentEditableWarning
            className={cn(
              editing && "cms-editable cms-editable-text",
              selection?.navId === item.id && "cms-selected",
            )}
            style={{
              fontSize: item.textStyle?.fontSize,
              fontWeight: item.textStyle?.fontWeight,
              color: item.textStyle?.color,
              letterSpacing:
                item.textStyle?.letterSpacing == null
                  ? undefined
                  : `${item.textStyle.letterSpacing}px`,
            }}
            data-cms-id={`nav:${item.id}`}
            data-cms-kind="nav"
            data-cms-nav={item.id}
            onClick={(event) => {
              if (!editing || preview) return;
              event.preventDefault();
              event.stopPropagation();
              select({
                kind: "nav",
                navId: item.id,
                field: "label",
                elementId: `nav:${item.id}`,
              });
            }}
            onBlur={(e) => {
              if (!editing || preview) return;
              updateSite((target) => {
                const nav = target.navigation.find((n) => n.id === item.id);
                if (nav)
                  nav.label = e.currentTarget.textContent?.slice(0, 80) ?? "";
              });
            }}
          >
            {item.label}
          </a>
        ))}
      </nav>
      <ModeSwitch />
      <button
        className="mobile-menu"
        onClick={() => setMenu(!menu)}
        aria-label="Toggle menu"
      >
        {menu ? <X /> : <Menu />}
      </button>
    </header>
  );
}

function Footer() {
  const { site } = useEditor();
  return (
    <footer id="contact">
      <div className="container footer-grid">
        <div>
          <BrandWordmark light />
          <GlobalEditableText field="footerDescription" as="p" />
        </div>
        <div>
          <GlobalEditableText field="contactLabel" as="small" />
          <a href={sanitizeUrl(`mailto:${site.globalContent.email}`)}>
            <GlobalEditableText field="email" as="span" />
          </a>
          <GlobalEditableText field="location" as="span" />
        </div>
        <div>
          <GlobalEditableText field="socialLabel" as="small" />
          <a href={sanitizeUrl(site.globalContent.instagramHref)}>
            <GlobalEditableText field="instagramLabel" as="span" />
          </a>
          <a href={sanitizeUrl(site.globalContent.linkedinHref)}>
            <GlobalEditableText field="linkedinLabel" as="span" />
          </a>
        </div>
      </div>
      <div className="container footer-bottom">
        <GlobalEditableText field="copyright" as="span" />
        <GlobalEditableText field="deliveryNote" as="span" />
        <a href="#top"><GlobalEditableText field="backToTop" as="span" /></a>
      </div>
    </footer>
  );
}

async function loadSession(): Promise<SiteConfig | null> {
  try {
    const response = await fetch("/api/session", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { site?: unknown };
    return validateSiteShape(data.site) ? data.site : null;
  } catch {
    return null;
  }
}

export default function App() {
  const {
    site,
    editing,
    preview,
    selection,
    select,
    onboardingSeen,
    reset,
    setSaveState,
    showToast,
    toast,
    hydrateRemote,
  } = useEditor();
  const [themeOpen, setThemeOpen] = useState(false),
    [addOpen, setAddOpen] = useState(false),
    [resetOpen, setResetOpen] = useState(false);
  const revealRef = useReveal();
  const hydrated = useRef(false);
  const saveTimer = useRef<number | null>(null);
  useEffect(() => {
    if (window.sessionStorage.getItem("sandbox-view-reload") === "1") {
      window.sessionStorage.removeItem("sandbox-view-reload");
      hydrated.current = true;
      return;
    }
    loadSession().then((remote) => {
      if (remote) hydrateRemote(remote);
      hydrated.current = true;
    });
  }, [hydrateRemote]);
  useEffect(() => {
    if (!hydrated.current) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = window.setTimeout(async () => {
      try {
        const response = await fetch("/api/session", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ site }),
        });
        setSaveState(response.ok ? "saved" : "offline");
      } catch {
        setSaveState("offline");
      }
    }, 900);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [site, setSaveState]);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => showToast(null), 2800);
    return () => window.clearTimeout(timer);
  }, [toast, showToast]);
  useEffect(() => {
    const keys = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (mod && event.key.toLowerCase() === "z") {
        event.preventDefault();
        event.shiftKey
          ? useEditor.getState().redo()
          : useEditor.getState().undo();
      }
      if (event.key === "Escape") {
        select(null);
        setAddOpen(false);
        setThemeOpen(false);
      }
      if (mod && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setAddOpen(true);
      }
    };
    window.addEventListener("keydown", keys);
    return () => window.removeEventListener("keydown", keys);
  }, [select]);
  const theme = site.theme;
  return (
    <div
      id="top"
      ref={revealRef}
      className={cn("app", editing && "editing", preview && "preview")}
      style={
        {
          "--bg": theme.background,
          "--surface": theme.surface,
          "--text": theme.text,
          "--muted": theme.muted,
          "--primary": theme.primary,
          "--secondary": theme.secondary,
          "--accent": theme.accent,
          "--button-text": theme.buttonText,
          "--border": theme.border,
          "--radius": `${theme.radius}px`,
        } as React.CSSProperties
      }
      onClickCapture={(event) => {
        if (!editing || preview) return;
        const target = (event.target as HTMLElement).closest<HTMLElement>(
          "[data-cms-id]",
        );
        if (!target?.dataset.cmsKind) return;
        const kind = target.dataset.cmsKind;
        if (kind === "nav") {
          select({ kind: "nav", navId: target.dataset.cmsNav, field: "label", elementId: target.dataset.cmsId });
          return;
        }
        if (kind === "global") {
          select({ kind: "global", field: target.dataset.cmsField, elementId: target.dataset.cmsId });
          return;
        }
        if (!target.dataset.cmsSection) return;
        select({
          kind: kind as "text" | "image" | "icon" | "link" | "item",
          sectionId: target.dataset.cmsSection,
          itemId: target.dataset.cmsItem || undefined,
          field: target.dataset.cmsField,
          linkField: target.dataset.cmsLinkField,
          elementId: target.dataset.cmsId,
        });
      }}
    >
      <Header />
      {editing && !preview && <EditorRail onTheme={() => setThemeOpen(true)} />}
      <main className="site-main">
        {site.sections.map((section) => {
          const Component = SECTION_REGISTRY[section.type];
          if (!Component) return null;
          const selected =
            selection?.sectionId === section.id && selection.kind === "section";
          return (
            <section
              id={section.id}
              key={section.id}
              data-reveal
              className={cn(
                "site-section",
                `section-${section.type}`,
                `anim-${section.animation.name}`,
                section.id === "hero" && "revealed",
                selected && "cms-selected",
              )}
              style={{
                ...animationStyle(section.animation),
                background: section.style.background,
                color: section.style.color,
                paddingBlock: section.style.padding,
                borderRadius: section.style.radius,
                boxShadow: section.style.shadow
                  ? `0 ${section.style.shadow / 2}px ${section.style.shadow}px rgb(0 0 0 / .12)`
                  : undefined,
              }}
              onClick={(event) => {
                if (editing && !preview && event.target === event.currentTarget)
                  select({ sectionId: section.id, kind: "section" });
              }}
            >
              {editing && !preview && (
                <button
                  className="section-handle"
                  draggable
                  onDragStart={(e) =>
                    e.dataTransfer.setData("application/x-section", section.id)
                  }
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) =>
                    useEditor
                      .getState()
                      .reorderSections(
                        e.dataTransfer.getData("application/x-section"),
                        section.id,
                      )
                  }
                  onClick={() =>
                    select({ sectionId: section.id, kind: "section" })
                  }
                >
                  <GripVertical /> {section.type.replace("-", " ")}
                </button>
              )}
              <Component section={section} />
            </section>
          );
        })}
      </main>
      <Footer />
      {editing && !preview && (
        <>
          <ContextPanel />
          {themeOpen && <ThemePanel onClose={() => setThemeOpen(false)} />}
          <EditorAddButton onClick={() => setAddOpen(!addOpen)} />
          {addOpen && <AddMenu onClose={() => setAddOpen(false)} />}
        </>
      )}
      <FloatingToolbar onReset={() => setResetOpen(true)} />
      {editing && !onboardingSeen && <Onboarding />}
      {resetOpen && (
        <ConfirmReset
          onCancel={() => setResetOpen(false)}
          onConfirm={() => {
            reset();
            setResetOpen(false);
            fetch("/api/session", { method: "DELETE" }).catch(() => undefined);
          }}
        />
      )}
      {toast && (
        <div className="toast">
          <span>✓</span>
          {toast}
        </div>
      )}
      {!editing && (
        <button className="floating-reset" onClick={() => setResetOpen(true)}>
          <RotateCcw /> Reset Demo
        </button>
      )}
      {editing && !preview && (
        <button className="floating-theme" onClick={() => setThemeOpen(true)}>
          <Palette /> Theme
        </button>
      )}
      <a className="scroll-top" href="#top" aria-label="Back to top">
        <ChevronDown />
      </a>
    </div>
  );
}
